import {Flag, Trend} from 'platform/components';
import {useFormatNumber} from 'platform/locale';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

// The platform Trend label span doesn't set its own font-weight, so wrapping it
// in a bold container makes the percentage text bold (the arrow icon is an SVG,
// unaffected). Keeps us off a platform-component style override.
// eslint-disable-next-line eag/no-css-property -- Muted pre-existing violation
const BoldTrend = styled.div`
  font-weight: ${({theme}) => theme.fontWeights.bold};
`;

interface PayPerUseTrendProps extends RequiredTestIdProps {
  // null → new this period; 0 → unchanged; otherwise the percentage delta.
  delta: number | null;
  // On a dark background the transparent (text-only) trend is low contrast, so
  // render the filled chip variant (light background) to stay legible.
  isOnDarkBackground?: boolean;
}

export function PayPerUseTrend(props: PayPerUseTrendProps) {
  const formatNumber = useFormatNumber();

  const formatDelta = (delta: number) => `${delta > 0 ? '+' : ''}${formatNumber(delta)} %`;
  const isTransparent = !props.isOnDarkBackground;

  const trend = match(props.delta)
    .with(Pattern.nullish, () => (
      <Flag
        label={i18n.t('general.labels.new')}
        colorScheme="blue"
        data-testid={suffixTestId('new', props)}
      />
    ))
    .with(Pattern.number.gt(0), (delta) => (
      <Trend
        label={formatDelta(delta)}
        isPositive
        isTransparent={isTransparent}
        data-testid={suffixTestId('positive', props)}
      />
    ))
    .with(Pattern.number.lt(0), (delta) => (
      <Trend
        label={formatDelta(delta)}
        isNegative
        isTransparent={isTransparent}
        data-testid={suffixTestId('negative', props)}
      />
    ))
    .otherwise((delta) => (
      <Trend
        label={formatDelta(delta)}
        isSubtle
        isTransparent={isTransparent}
        data-testid={suffixTestId('neutral', props)}
      />
    ));

  return <BoldTrend>{trend}</BoldTrend>;
}
